import type { QuestionnaireChatMessage } from "@digital-twins/shared-types";
import type { TFunction } from "i18next";

import { builderCardText } from "../utils/builderCardCopy";
import {
  notDoneLines,
  removedObjectiveQuestionLines,
} from "../utils/notDoneCopy";
import { ruleCopy } from "../utils/questionnaireIssueCopy";
import { languageName, objectiveChangeLines } from "../utils/turnCommandCopy";
import { userMessageReceiptLines } from "../utils/userMessageReceiptLines";

/** What a reply's lines are worded from. */
export type AssistantReplyMessage = Pick<
  QuestionnaireChatMessage,
  | "id"
  | "content"
  | "notDone"
  | "removedObjectiveQuestions"
  | "objectiveChange"
  | "translatedInto"
  | "refusals"
  | "materialCollectionCancelled"
  | "notInStudyLanguage"
  | "collect"
>;

/**
 * A reply's lines, the one composition every assistant message reads: its
 * bubble (`ChatBubble`) and the card a reply with an upload carries
 * (`CollectCard`, gate 2026-10-15 PR-09). A reply's content is the model's own
 * line; what its batch changed of the objective, what the turn did not do,
 * and the questions an objective edit kept for a removed objective are typed
 * on the message and worded here (QA4 PN N7, F16, F24).
 */
export function assistantReplyLines(
  message: AssistantReplyMessage,
  chainCode: string | null | undefined,
  assetPanelHeading: string,
  t: TFunction<"app">,
): Array<string> {
  return [
    // A translation's reply is fixed: the builder's words, in the
    // interface language; its `content` is the commit's fixed line.
    message.translatedInto
      ? t("questionnaire.translatedReply", {
          language: languageName(message.translatedInto),
        })
      : // A cancelled material collection's reply is the builder's
        // words too (C12-1); `content` is its English copy.
        message.materialCollectionCancelled
        ? t("questionnaire.materialReceipt.cancelled")
        : // A question Nest asks by a catalog rule is the rule's line, in
          // the interface language; `content` is its English copy.
          message.refusals
          ? message.refusals
              .map(({ code, params }) => ruleCopy(code, params))
              .join(" ")
          : builderCardText(message, chainCode, t),
    ...(message.objectiveChange
      ? objectiveChangeLines(message.objectiveChange)
      : []),
    ...(message.notDone
      ? notDoneLines(
          message.notDone,
          assetPanelHeading,
          t,
          Boolean(message.collect),
        )
      : []),
    ...removedObjectiveQuestionLines(message.removedObjectiveQuestions ?? [], t),
    // Material a chat revision or re-plan added in another language is
    // kept as written (C12-1): the reply's typed receipt, as a paste's.
    ...userMessageReceiptLines(
      { notInStudyLanguage: message.notInStudyLanguage },
      t,
    ),
  ];
}

/** A message's lines as a bubble shows them: a `- ` line is a list item. */
export function ReplyLines(props: { id: string; text: string }) {
  const { id, text } = props;
  return (
    <>
      {text.split("\n").map((line, index) => {
        const bulletMatch = /^[-*•]\s+(.*)$/.exec(line);
        if (bulletMatch) {
          return (
            <span key={`${id}-${index}`} className="flex gap-2">
              <span aria-hidden="true" className="shrink-0">
                •
              </span>
              <span className="min-w-0">{bulletMatch[1]}</span>
            </span>
          );
        }
        if (line.length === 0) {
          return (
            <span
              key={`${id}-${index}`}
              className="block h-2"
              aria-hidden="true"
            />
          );
        }
        return (
          <span key={`${id}-${index}`} className="block">
            {line}
          </span>
        );
      })}
    </>
  );
}
